/**
 * FERP Core Normalizers
 *
 * DRR-027: Normalizers for FERP primitive types (Task, Workflow, Actor, Queue)
 *
 * These normalizers convert FERP core primitive types into NormalizedEntity<T>
 * with full type preservation and life-safety enforcement.
 */

import type {
  Task,
  Workflow,
  Actor,
  Queue,
  Constraint,
  Notification,
  Policy,
  Schedule,
  Event,
  Contract,
  Dependency,
  // New primitive types for DRR-027
  Template,
  Role,
  Team,
  Validation,
  Instance,
  Approval,
  Document,
  Trace,
  Checkpoint,
  Transaction,
  AuditQl,
  Analytics,
  Resource,
  Integration,
} from '@/generated/graphql';

import { SafetyLevel } from '@/types/graphql-compat';

import type {
  NormalizedEntity,
  ComplianceFramework,
} from '@/types/normalized-entity';

import { UnifiedStatus } from '@/types/normalized-entity';

// ============================================================================
// STATUS MAPPINGS
// ============================================================================

/**
 * Map GqlTaskStatus to UnifiedStatus
 */
/**
 * Map GqlTaskStatus string to UnifiedStatus with fallback for unknown values.
 * Uses string keys to handle GraphQL codegen's %future added value pattern.
 */
const TASK_STATUS_MAP: Record<string, UnifiedStatus> = {
  PENDING: UnifiedStatus.PENDING,
  READY: UnifiedStatus.PENDING,
  ASSIGNED: UnifiedStatus.PENDING,
  IN_PROGRESS: UnifiedStatus.ACTIVE,
  PAUSED: UnifiedStatus.BLOCKED,
  PENDING_EXTERNAL: UnifiedStatus.BLOCKED,
  BLOCKED: UnifiedStatus.BLOCKED,
  COMPLETED: UnifiedStatus.COMPLETED,
  COMPENSATED: UnifiedStatus.COMPLETED,
  CANCELLED: UnifiedStatus.CANCELLED,
  FAILED: UnifiedStatus.FAILED,
};

/**
 * Map workflow criticality string to SafetyLevel
 */
function mapCriticalityToSafetyLevel(criticality: string): SafetyLevel {
  const criticalityLower = criticality.toLowerCase();

  if (criticalityLower === 'life_critical' || criticalityLower === 'life-critical') {
    return SafetyLevel.LIFE_CRITICAL;
  }
  if (criticalityLower === 'critical') {
    return SafetyLevel.CRITICAL;
  }
  if (criticalityLower === 'high') {
    return SafetyLevel.HIGH;
  }
  if (criticalityLower === 'medium' || criticalityLower === 'operational') {
    return SafetyLevel.MEDIUM;
  }
  if (criticalityLower === 'low') {
    return SafetyLevel.LOW;
  }

  // Default to HIGH for unknown criticality (safe default)
  return SafetyLevel.HIGH;
}

/**
 * Map priority to SafetyLevel
 * Works for GqlTaskPriority, GqlSchedulePriority, and similar enums.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapPriorityToSafetyLevel(priority: string): SafetyLevel {
  switch (priority) {
    case 'CRITICAL':
      return SafetyLevel.CRITICAL;
    case 'HIGH':
      return SafetyLevel.HIGH;
    case 'NORMAL':
      return SafetyLevel.MEDIUM;
    case 'LOW':
      return SafetyLevel.LOW;
    default:
      // Safe fallback for unknown priority values
      return SafetyLevel.MEDIUM;
  }
}

/**
 * Map string compliance frameworks to typed ComplianceFramework
 */
function mapComplianceFrameworks(frameworks: string[]): ComplianceFramework[] {
  const validFrameworks: ComplianceFramework[] = [];

  for (const framework of frameworks) {
    const upper = framework.toUpperCase();
    if (upper === 'HIPAA' || upper === 'SOX' || upper === 'PCI' || upper === 'GDPR') {
      validFrameworks.push(upper as ComplianceFramework);
    }
  }

  return validFrameworks;
}

/**
 * Determine if entity requires dual approval based on safety level
 */
function requiresDualApprovalForLevel(safetyLevel: SafetyLevel): boolean {
  return safetyLevel === SafetyLevel.LIFE_CRITICAL || safetyLevel === SafetyLevel.CRITICAL;
}

// ============================================================================
// TASK NORMALIZER
// ============================================================================

/**
 * Normalize a FERP Task into NormalizedEntity<Task>
 *
 * @param task - The Task from GraphQL
 * @returns Fully typed NormalizedEntity with Task preserved in domainData
 *
 * @example
 * ```typescript
 * const normalized = normalizeTask(task);
 * console.log(normalized.displayName);        // "Review insurance claim"
 * console.log(normalized.sourceSubgraph);     // "ferp-core"
 * console.log(normalized.safetyLevel);        // SafetyLevel.HIGH
 * console.log(normalized.domainData.status);  // GqlTaskStatus.IN_PROGRESS
 * ```
 */
export function normalizeTask(task: Task): NormalizedEntity<Task> {
  // Infer safety level from priority (Task doesn't have direct safetyLevel)
  const safetyLevel = mapPriorityToSafetyLevel(task.priority);

  // Map status to unified status
  const unifiedStatus = TASK_STATUS_MAP[task.status] ?? UnifiedStatus.PENDING;

  // Check if blocked by safety
  const isBlockedBySafety = task.status === 'BLOCKED' || task.status === 'PENDING_EXTERNAL';

  return {
    id: task.id,
    displayName: task.name || task.description || `Task ${task.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Task',
    safetyLevel,
    requiresDualApproval: requiresDualApprovalForLevel(safetyLevel),
    complianceFrameworks: [], // Tasks inherit from workflow
    unifiedStatus,
    isBlockedBySafety,
    domainData: task,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// WORKFLOW NORMALIZER
// ============================================================================

/**
 * Normalize a FERP Workflow into NormalizedEntity<Workflow>
 *
 * @param workflow - The Workflow from GraphQL
 * @returns Fully typed NormalizedEntity with Workflow preserved in domainData
 *
 * @example
 * ```typescript
 * const normalized = normalizeWorkflow(workflow);
 * console.log(normalized.displayName);              // "Patient Registration"
 * console.log(normalized.safetyLevel);              // SafetyLevel.LIFE_CRITICAL
 * console.log(normalized.complianceFrameworks);     // ["HIPAA"]
 * console.log(normalized.domainData.criticality);   // "life_critical"
 * ```
 */
export function normalizeWorkflow(workflow: Workflow): NormalizedEntity<Workflow> {
  // Map criticality to safety level
  const safetyLevel = mapCriticalityToSafetyLevel(workflow.criticality);

  // Map compliance frameworks
  const complianceFrameworks = mapComplianceFrameworks(workflow.complianceFrameworks);

  // Workflow active status to unified status
  const unifiedStatus = workflow.active ? UnifiedStatus.ACTIVE : UnifiedStatus.PENDING;

  return {
    id: workflow.id,
    displayName: workflow.name || `Workflow ${workflow.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Workflow',
    safetyLevel,
    requiresDualApproval: requiresDualApprovalForLevel(safetyLevel),
    complianceFrameworks,
    unifiedStatus,
    isBlockedBySafety: false, // Workflows don't have blocked state
    domainData: workflow,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// ACTOR NORMALIZER
// ============================================================================

/**
 * Normalize a FERP Actor into NormalizedEntity<Actor>
 *
 * @param actor - The Actor from GraphQL
 * @returns Fully typed NormalizedEntity with Actor preserved in domainData
 */
export function normalizeActor(actor: Actor): NormalizedEntity<Actor> {
  // Actors inherit safety level from context (default to MEDIUM)
  const safetyLevel = SafetyLevel.MEDIUM;

  // Map actor status to unified status
  let unifiedStatus: UnifiedStatus;
  switch (actor.status) {
    case 'ACTIVE':
      unifiedStatus = UnifiedStatus.ACTIVE;
      break;
    case 'SUSPENDED':
    case 'TERMINATED':
      unifiedStatus = UnifiedStatus.BLOCKED;
      break;
    case 'FAILED':
      unifiedStatus = UnifiedStatus.FAILED;
      break;
    default:
      unifiedStatus = UnifiedStatus.PENDING;
  }

  return {
    id: actor.id,
    displayName: actor.name || `Actor ${actor.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Actor',
    safetyLevel,
    requiresDualApproval: false, // Actors don't require dual approval by default
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety: actor.status === 'SUSPENDED' || actor.status === 'TERMINATED',
    domainData: actor,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// QUEUE NORMALIZER
// ============================================================================

/**
 * Normalize a FERP Queue into NormalizedEntity<Queue>
 *
 * @param queue - The Queue from GraphQL
 * @returns Fully typed NormalizedEntity with Queue preserved in domainData
 */
export function normalizeQueue(queue: Queue): NormalizedEntity<Queue> {
  // Queues inherit safety level from their configuration
  // Default to HIGH since queues can affect multiple tasks
  const safetyLevel = SafetyLevel.HIGH;

  // Map queue status to unified status
  let unifiedStatus: UnifiedStatus;
  const status = (queue as { status?: string }).status;

  switch (status) {
    case 'ACTIVE':
      unifiedStatus = UnifiedStatus.ACTIVE;
      break;
    case 'PAUSED':
    case 'DRAINING':
      unifiedStatus = UnifiedStatus.BLOCKED;
      break;
    case 'STOPPED':
      unifiedStatus = UnifiedStatus.CANCELLED;
      break;
    default:
      unifiedStatus = UnifiedStatus.ACTIVE;
  }

  return {
    id: queue.id,
    displayName: queue.queueName || `Queue ${queue.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Queue',
    safetyLevel,
    requiresDualApproval: false,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety: status === 'PAUSED' || status === 'STOPPED',
    domainData: queue,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// CONSTRAINT NORMALIZER
// ============================================================================

/**
 * Map GqlConstraintStatus to UnifiedStatus.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapConstraintStatus(status: string): UnifiedStatus {
  switch (status) {
    case 'ACTIVE':
      return UnifiedStatus.ACTIVE;
    case 'WARNING':
      return UnifiedStatus.PENDING;
    case 'VIOLATED':
    case 'DISABLED':
      return UnifiedStatus.BLOCKED;
    default:
      // Safe fallback for unknown constraint status values
      return UnifiedStatus.PENDING;
  }
}

/**
 * Normalize a FERP Constraint into NormalizedEntity<Constraint>
 *
 * @param constraint - The Constraint from GraphQL
 * @returns Fully typed NormalizedEntity with Constraint preserved in domainData
 */
export function normalizeConstraint(constraint: Constraint): NormalizedEntity<Constraint> {
  // Constraints are high safety level by default (they enforce business rules)
  const safetyLevel = SafetyLevel.HIGH;

  // Map constraint status to unified status
  const unifiedStatus = constraint.active
    ? mapConstraintStatus(constraint.currentStatus)
    : UnifiedStatus.PENDING;

  // Check if blocked by safety
  const isBlockedBySafety = constraint.currentStatus === 'VIOLATED' || constraint.currentStatus === 'DISABLED';

  return {
    id: constraint.id,
    displayName: constraint.name || `Constraint ${constraint.code}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Constraint',
    safetyLevel,
    requiresDualApproval: constraint.overrideRequiresReason,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety,
    domainData: constraint,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// NOTIFICATION NORMALIZER
// ============================================================================

/**
 * Map notification status to UnifiedStatus
 */
function mapNotificationStatus(notification: Notification): UnifiedStatus {
  // Check delivery status
  if (notification.deliveredAt) {
    return UnifiedStatus.COMPLETED;
  }
  if (notification.archived) {
    return UnifiedStatus.CANCELLED;
  }
  if (notification.lastDeliveryError) {
    return UnifiedStatus.FAILED;
  }
  if (notification.readAt) {
    return UnifiedStatus.ACTIVE;
  }
  return UnifiedStatus.PENDING;
}

/**
 * Map notification priority to SafetyLevel.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapNotificationPriorityToSafetyLevel(priority: string): SafetyLevel {
  switch (priority) {
    case 'CRITICAL':
      return SafetyLevel.CRITICAL;
    case 'HIGH':
      return SafetyLevel.HIGH;
    case 'MEDIUM':
      return SafetyLevel.MEDIUM;
    case 'LOW':
    case 'INFO':
      return SafetyLevel.LOW;
    default:
      // Safe fallback for unknown priority values
      return SafetyLevel.MEDIUM;
  }
}

/**
 * Normalize a FERP Notification into NormalizedEntity<Notification>
 *
 * @param notification - The Notification from GraphQL
 * @returns Fully typed NormalizedEntity with Notification preserved in domainData
 */
export function normalizeNotification(notification: Notification): NormalizedEntity<Notification> {
  // Map priority to safety level
  const safetyLevel = mapNotificationPriorityToSafetyLevel(notification.priority);

  // Map delivery status to unified status
  const unifiedStatus = mapNotificationStatus(notification);

  // Map compliance frameworks
  const complianceFrameworks = mapComplianceFrameworks(notification.complianceFrameworks);

  return {
    id: notification.id,
    displayName: notification.notificationTitle || `Notification ${notification.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Notification',
    safetyLevel,
    requiresDualApproval: requiresDualApprovalForLevel(safetyLevel),
    complianceFrameworks,
    unifiedStatus,
    isBlockedBySafety: false, // Notifications don't have blocked state
    domainData: notification,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// POLICY NORMALIZER
// ============================================================================

/**
 * Normalize a FERP Policy into NormalizedEntity<Policy>
 *
 * @param policy - The Policy from GraphQL
 * @returns Fully typed NormalizedEntity with Policy preserved in domainData
 */
export function normalizePolicy(policy: Policy): NormalizedEntity<Policy> {
  // Policies enforce rules, so default to HIGH safety level
  const safetyLevel = SafetyLevel.HIGH;

  // Map active/enabled status to unified status
  let unifiedStatus: UnifiedStatus;
  if (!policy.active) {
    unifiedStatus = UnifiedStatus.CANCELLED;
  } else if (policy.enabled) {
    unifiedStatus = UnifiedStatus.ACTIVE;
  } else {
    unifiedStatus = UnifiedStatus.PENDING;
  }

  return {
    id: policy.id,
    displayName: policy.name || `Policy ${policy.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Policy',
    safetyLevel,
    requiresDualApproval: false, // Policies don't require dual approval by default
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety: false,
    domainData: policy,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// SCHEDULE NORMALIZER
// ============================================================================

/**
 * Map GqlScheduleStatus to UnifiedStatus.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapScheduleStatus(status: string): UnifiedStatus {
  switch (status) {
    case 'ACTIVE':
      return UnifiedStatus.ACTIVE;
    case 'PAUSED':
      return UnifiedStatus.BLOCKED;
    case 'DISABLED':
      return UnifiedStatus.CANCELLED;
    case 'EXPIRED':
      return UnifiedStatus.COMPLETED;
    case 'FAILED':
      return UnifiedStatus.FAILED;
    default:
      // Safe fallback for unknown schedule status values
      return UnifiedStatus.PENDING;
  }
}

/**
 * Normalize a FERP Schedule into NormalizedEntity<Schedule>
 *
 * @param schedule - The Schedule from GraphQL
 * @returns Fully typed NormalizedEntity with Schedule preserved in domainData
 */
export function normalizeSchedule(schedule: Schedule): NormalizedEntity<Schedule> {
  // Use isLifeCritical flag if set, otherwise infer from priority
  const safetyLevel = schedule.isLifeCritical
    ? SafetyLevel.LIFE_CRITICAL
    : mapPriorityToSafetyLevel(schedule.schedulePriority);

  // Map schedule status to unified status
  const unifiedStatus = mapScheduleStatus(schedule.scheduleStatus);

  // Check if blocked by safety
  const isBlockedBySafety = schedule.scheduleStatus === 'PAUSED' || schedule.scheduleStatus === 'DISABLED';

  return {
    id: schedule.scheduleId,
    displayName: schedule.scheduleName || schedule.scheduleCode || `Schedule ${schedule.scheduleId}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Schedule',
    safetyLevel,
    requiresDualApproval: schedule.isLifeCritical,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety,
    domainData: schedule,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// EVENT NORMALIZER
// ============================================================================

/**
 * Map GqlEventCategory to SafetyLevel.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapEventCategoryToSafetyLevel(category: string): SafetyLevel {
  switch (category) {
    case 'SECURITY':
      return SafetyLevel.CRITICAL;
    case 'COMPLIANCE':
      return SafetyLevel.HIGH;
    case 'WORKFLOW':
    case 'DATA':
      return SafetyLevel.MEDIUM;
    case 'PERFORMANCE':
    case 'SYSTEM':
    case 'USER':
      return SafetyLevel.LOW;
    default:
      // Safe fallback for unknown event category values
      return SafetyLevel.MEDIUM;
  }
}

/**
 * Normalize a FERP Event into NormalizedEntity<Event>
 *
 * @param event - The Event from GraphQL
 * @returns Fully typed NormalizedEntity with Event preserved in domainData
 */
export function normalizeEvent(event: Event): NormalizedEntity<Event> {
  // Map category to safety level
  const safetyLevel = mapEventCategoryToSafetyLevel(event.category);

  // Events don't have a status field - use archived flag
  const unifiedStatus = event.archived ? UnifiedStatus.COMPLETED : UnifiedStatus.ACTIVE;

  // Map compliance frameworks
  const complianceFrameworks = mapComplianceFrameworks(event.complianceFrameworks);

  return {
    id: event.id,
    displayName: event.eventName || `Event ${event.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Event',
    safetyLevel,
    requiresDualApproval: false, // Events are immutable, no approval needed
    complianceFrameworks,
    unifiedStatus,
    isBlockedBySafety: false, // Events can't be blocked
    domainData: event,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// CONTRACT NORMALIZER
// ============================================================================

/**
 * Map GqlContractStatus to UnifiedStatus.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapContractStatus(status: string): UnifiedStatus {
  switch (status) {
    case 'ACTIVE':
      return UnifiedStatus.ACTIVE;
    case 'MET':
      return UnifiedStatus.COMPLETED;
    case 'PAUSED':
      return UnifiedStatus.BLOCKED;
    case 'WARNING':
      return UnifiedStatus.PENDING;
    case 'BREACHED':
      return UnifiedStatus.FAILED;
    case 'ARCHIVED':
      return UnifiedStatus.CANCELLED;
    default:
      // Safe fallback for unknown contract status values
      return UnifiedStatus.PENDING;
  }
}

/**
 * Normalize a FERP Contract into NormalizedEntity<Contract>
 *
 * @param contract - The Contract from GraphQL
 * @returns Fully typed NormalizedEntity with Contract preserved in domainData
 */
export function normalizeContract(contract: Contract): NormalizedEntity<Contract> {
  // Contracts (SLAs) are high safety by default
  const safetyLevel = SafetyLevel.HIGH;

  // Map contract status to unified status
  const unifiedStatus = mapContractStatus(contract.currentStatus);

  // Check if blocked by safety
  const isBlockedBySafety = contract.paused || contract.currentStatus === 'BREACHED';

  return {
    id: contract.id,
    displayName: contract.slaName || `Contract ${contract.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Contract',
    safetyLevel,
    requiresDualApproval: false,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety,
    domainData: contract,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// DEPENDENCY NORMALIZER
// ============================================================================

/**
 * Map GqlDependencyStatus to UnifiedStatus.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapDependencyStatus(status: string): UnifiedStatus {
  switch (status) {
    case 'SATISFIED':
      return UnifiedStatus.COMPLETED;
    case 'IN_PROGRESS':
      return UnifiedStatus.ACTIVE;
    case 'PENDING':
      return UnifiedStatus.PENDING;
    case 'BYPASSED':
      return UnifiedStatus.CANCELLED;
    case 'FAILED':
      return UnifiedStatus.FAILED;
    default:
      // Safe fallback for unknown dependency status values
      return UnifiedStatus.PENDING;
  }
}

/**
 * Normalize a FERP Dependency into NormalizedEntity<Dependency>
 *
 * @param dependency - The Dependency from GraphQL
 * @returns Fully typed NormalizedEntity with Dependency preserved in domainData
 */
export function normalizeDependency(dependency: Dependency): NormalizedEntity<Dependency> {
  // Dependencies control workflow flow, so default to HIGH safety
  const safetyLevel = SafetyLevel.HIGH;

  // Map dependency status to unified status
  const unifiedStatus = dependency.active
    ? mapDependencyStatus(dependency.status)
    : UnifiedStatus.CANCELLED;

  // Check if blocked by safety
  const isBlockedBySafety = dependency.status === 'FAILED' && !dependency.canBypass;

  return {
    id: dependency.id,
    displayName: dependency.name || `Dependency ${dependency.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Dependency',
    safetyLevel,
    requiresDualApproval: !dependency.canBypass, // If can't bypass, may need dual approval
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety,
    domainData: dependency,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// TEMPLATE NORMALIZER (DRR-027)
// ============================================================================

/**
 * Map GqlTemplateStatus to UnifiedStatus.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapTemplateStatus(status: string): UnifiedStatus {
  switch (status) {
    case 'PUBLISHED':
      return UnifiedStatus.ACTIVE;
    case 'DRAFT':
      return UnifiedStatus.PENDING;
    case 'PENDING_APPROVAL':
      return UnifiedStatus.BLOCKED;
    case 'ARCHIVED':
      return UnifiedStatus.CANCELLED;
    default:
      // Safe fallback for unknown template status values
      return UnifiedStatus.PENDING;
  }
}

/**
 * Normalize a FERP Template into NormalizedEntity<Template>
 *
 * @param template - The Template from GraphQL
 * @returns Fully typed NormalizedEntity with Template preserved in domainData
 */
export function normalizeTemplate(template: Template): NormalizedEntity<Template> {
  // Map criticality to safety level
  const safetyLevel = mapCriticalityToSafetyLevel(template.criticality);

  // Map template status to unified status
  const unifiedStatus = template.active
    ? mapTemplateStatus(template.status)
    : UnifiedStatus.CANCELLED;

  // Map compliance frameworks
  const complianceFrameworks = mapComplianceFrameworks(template.complianceFrameworks);

  return {
    id: template.id,
    displayName: template.name || `Template ${template.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Template',
    safetyLevel,
    requiresDualApproval: requiresDualApprovalForLevel(safetyLevel),
    complianceFrameworks,
    unifiedStatus,
    isBlockedBySafety: template.status === 'PENDING_APPROVAL',
    domainData: template,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// ROLE NORMALIZER (DRR-027)
// ============================================================================

/**
 * Normalize a FERP Role into NormalizedEntity<Role>
 *
 * @param role - The Role from GraphQL
 * @returns Fully typed NormalizedEntity with Role preserved in domainData
 */
export function normalizeRole(role: Role): NormalizedEntity<Role> {
  // Roles are HIGH safety by default (they control access)
  const safetyLevel = SafetyLevel.HIGH;

  // Map role active status to unified status
  const unifiedStatus = role.active ? UnifiedStatus.ACTIVE : UnifiedStatus.CANCELLED;

  return {
    id: role.id,
    displayName: role.roleName || `Role ${role.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Role',
    safetyLevel,
    requiresDualApproval: false,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety: false,
    domainData: role,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// TEAM NORMALIZER (DRR-027)
// ============================================================================

/**
 * Normalize a FERP Team into NormalizedEntity<Team>
 *
 * @param team - The Team from GraphQL
 * @returns Fully typed NormalizedEntity with Team preserved in domainData
 */
export function normalizeTeam(team: Team): NormalizedEntity<Team> {
  // Teams are MEDIUM safety by default
  const safetyLevel = SafetyLevel.MEDIUM;

  // Map team active status to unified status
  const unifiedStatus = team.active ? UnifiedStatus.ACTIVE : UnifiedStatus.CANCELLED;

  return {
    id: team.id,
    displayName: team.name || team.code || `Team ${team.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Team',
    safetyLevel,
    requiresDualApproval: false,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety: false,
    domainData: team,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// VALIDATION NORMALIZER (DRR-027)
// ============================================================================

/**
 * Map GqlValidationSeverity to SafetyLevel.
 * GqlValidationSeverity has: CRITICAL, ERROR, WARNING, INFO.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapValidationSeverityToSafetyLevel(severity: string): SafetyLevel {
  switch (severity) {
    case 'CRITICAL':
      return SafetyLevel.CRITICAL;
    case 'ERROR':
      return SafetyLevel.HIGH;
    case 'WARNING':
      return SafetyLevel.MEDIUM;
    case 'INFO':
      return SafetyLevel.LOW;
    default:
      // Safe fallback for unknown severity values
      return SafetyLevel.MEDIUM;
  }
}

/**
 * Normalize a FERP Validation into NormalizedEntity<Validation>
 *
 * @param validation - The Validation from GraphQL
 * @returns Fully typed NormalizedEntity with Validation preserved in domainData
 */
export function normalizeValidation(validation: Validation): NormalizedEntity<Validation> {
  // Map severity to safety level
  const safetyLevel = mapValidationSeverityToSafetyLevel(validation.severity);

  // Map validation active status to unified status
  const unifiedStatus = validation.active ? UnifiedStatus.ACTIVE : UnifiedStatus.CANCELLED;

  // Map compliance frameworks
  const complianceFrameworks = mapComplianceFrameworks(validation.complianceFrameworks);

  return {
    id: validation.id,
    displayName: validation.name || validation.code || `Validation ${validation.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Validation',
    safetyLevel,
    requiresDualApproval: validation.isBlocking && safetyLevel === SafetyLevel.CRITICAL,
    complianceFrameworks,
    unifiedStatus,
    isBlockedBySafety: validation.isBlocking,
    domainData: validation,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// INSTANCE NORMALIZER (DRR-027)
// ============================================================================

/**
 * Map GqlInstanceStatus to UnifiedStatus.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapInstanceStatus(status: string): UnifiedStatus {
  switch (status) {
    case 'RUNNING':
      return UnifiedStatus.ACTIVE;
    case 'PENDING':
      return UnifiedStatus.PENDING;
    case 'COMPLETED':
    case 'COMPENSATED':
      return UnifiedStatus.COMPLETED;
    case 'FAILED':
    case 'TIMED_OUT':
      return UnifiedStatus.FAILED;
    case 'CANCELLED':
      return UnifiedStatus.CANCELLED;
    case 'PAUSED':
    case 'COMPENSATING':
      return UnifiedStatus.BLOCKED;
    default:
      // Safe fallback for unknown instance status values
      return UnifiedStatus.PENDING;
  }
}

/**
 * Normalize a FERP Instance into NormalizedEntity<Instance>
 *
 * @param instance - The Instance from GraphQL
 * @returns Fully typed NormalizedEntity with Instance preserved in domainData
 */
export function normalizeInstance(instance: Instance): NormalizedEntity<Instance> {
  // Instances are HIGH safety by default (they're running workflows)
  const safetyLevel = SafetyLevel.HIGH;

  // Map instance status to unified status
  const unifiedStatus = mapInstanceStatus(instance.status);

  // Check if blocked by safety
  const isBlockedBySafety = instance.status === 'PAUSED' || instance.status === 'COMPENSATING';

  return {
    id: instance.id,
    displayName: instance.instanceName || `Instance ${instance.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Instance',
    safetyLevel,
    requiresDualApproval: false,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety,
    domainData: instance,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// APPROVAL NORMALIZER (DRR-027)
// ============================================================================

/**
 * Map GqlApprovalStatus to UnifiedStatus.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapApprovalStatus(status: string): UnifiedStatus {
  switch (status) {
    case 'IN_REVIEW':
      return UnifiedStatus.ACTIVE;
    case 'PENDING':
      return UnifiedStatus.PENDING;
    case 'APPROVED':
      return UnifiedStatus.COMPLETED;
    case 'REJECTED':
    case 'EXPIRED':
      return UnifiedStatus.FAILED;
    case 'ESCALATED':
    case 'REVISION_REQUESTED':
      return UnifiedStatus.BLOCKED;
    default:
      // Safe fallback for unknown approval status values
      return UnifiedStatus.PENDING;
  }
}

/**
 * Map GqlApprovalPriority to SafetyLevel.
 * GqlApprovalPriority has: CRITICAL, HIGH, MEDIUM, LOW.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapApprovalPriorityToSafetyLevel(priority: string): SafetyLevel {
  switch (priority) {
    case 'CRITICAL':
      return SafetyLevel.CRITICAL;
    case 'HIGH':
      return SafetyLevel.HIGH;
    case 'MEDIUM':
      return SafetyLevel.MEDIUM;
    case 'LOW':
      return SafetyLevel.LOW;
    default:
      // Safe fallback for unknown priority values
      return SafetyLevel.MEDIUM;
  }
}

/**
 * Normalize a FERP Approval into NormalizedEntity<Approval>
 *
 * @param approval - The Approval from GraphQL
 * @returns Fully typed NormalizedEntity with Approval preserved in domainData
 */
export function normalizeApproval(approval: Approval): NormalizedEntity<Approval> {
  // Map priority to safety level
  const safetyLevel = mapApprovalPriorityToSafetyLevel(approval.priority);

  // Map approval status to unified status
  const unifiedStatus = mapApprovalStatus(approval.status);

  // Check if blocked by safety
  const isBlockedBySafety = approval.status === 'ESCALATED' || approval.status === 'REVISION_REQUESTED';

  return {
    id: approval.id,
    displayName: approval.approvalName || `Approval ${approval.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Approval',
    safetyLevel,
    requiresDualApproval: approval.requiredApprovals > 1,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety,
    domainData: approval,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// DOCUMENT NORMALIZER (DRR-027)
// ============================================================================

/**
 * Map DocumentStatus to UnifiedStatus.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapDocumentStatus(status: string): UnifiedStatus {
  switch (status) {
    case 'APPROVED':
      return UnifiedStatus.COMPLETED;
    case 'DRAFT':
      return UnifiedStatus.PENDING;
    case 'PENDING_APPROVAL':
    case 'PENDING_VALIDATION':
      return UnifiedStatus.BLOCKED;
    case 'ARCHIVED':
      return UnifiedStatus.CANCELLED;
    default:
      // Safe fallback for unknown document status values
      return UnifiedStatus.PENDING;
  }
}

/**
 * Normalize a FERP Document into NormalizedEntity<Document>
 *
 * @param document - The Document from GraphQL
 * @returns Fully typed NormalizedEntity with Document preserved in domainData
 */
export function normalizeDocument(document: Document): NormalizedEntity<Document> {
  // Documents are MEDIUM safety by default
  const safetyLevel = SafetyLevel.MEDIUM;

  // Map document status to unified status
  const unifiedStatus = document.isDeleted
    ? UnifiedStatus.CANCELLED
    : mapDocumentStatus(document.currentStatus);

  // Map compliance frameworks
  const complianceFrameworks = mapComplianceFrameworks(document.complianceFrameworks);

  // Check if blocked by safety
  const isBlockedBySafety = document.currentStatus === 'PENDING_APPROVAL' || document.currentStatus === 'PENDING_VALIDATION';

  return {
    id: document.documentId,
    displayName: document.documentName || `Document ${document.documentId}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Document',
    safetyLevel,
    requiresDualApproval: false,
    complianceFrameworks,
    unifiedStatus,
    isBlockedBySafety,
    domainData: document,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// TRACE NORMALIZER (DRR-027)
// ============================================================================

/**
 * Normalize a FERP Trace into NormalizedEntity<Trace>
 *
 * @param trace - The Trace from GraphQL
 * @returns Fully typed NormalizedEntity with Trace preserved in domainData
 */
export function normalizeTrace(trace: Trace): NormalizedEntity<Trace> {
  // Traces are LOW safety by default (they're observability data)
  const safetyLevel = SafetyLevel.LOW;

  // Map trace status based on error presence and completion
  let unifiedStatus: UnifiedStatus;
  if (trace.errorCode || trace.errorMessage) {
    unifiedStatus = UnifiedStatus.FAILED;
  } else if (trace.endedAt) {
    unifiedStatus = UnifiedStatus.COMPLETED;
  } else {
    unifiedStatus = UnifiedStatus.ACTIVE;
  }

  return {
    id: trace.id,
    displayName: trace.operationName || `Trace ${trace.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Trace',
    safetyLevel,
    requiresDualApproval: false,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety: false,
    domainData: trace,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// CHECKPOINT NORMALIZER (DRR-027)
// ============================================================================

/**
 * Map GqlCheckpointStatus to UnifiedStatus.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapCheckpointStatus(status: string): UnifiedStatus {
  switch (status) {
    case 'AVAILABLE':
      return UnifiedStatus.ACTIVE;
    case 'ROLLED_BACK':
      return UnifiedStatus.COMPLETED;
    case 'ARCHIVED':
    case 'EXPIRED':
      return UnifiedStatus.CANCELLED;
    case 'CORRUPTED':
      return UnifiedStatus.FAILED;
    default:
      // Safe fallback for unknown checkpoint status values
      return UnifiedStatus.PENDING;
  }
}

/**
 * Normalize a FERP Checkpoint into NormalizedEntity<Checkpoint>
 *
 * @param checkpoint - The Checkpoint from GraphQL
 * @returns Fully typed NormalizedEntity with Checkpoint preserved in domainData
 */
export function normalizeCheckpoint(checkpoint: Checkpoint): NormalizedEntity<Checkpoint> {
  // Checkpoints are HIGH safety by default (they enable recovery)
  const safetyLevel = SafetyLevel.HIGH;

  // Map checkpoint status to unified status
  const unifiedStatus = mapCheckpointStatus(checkpoint.currentStatus);

  // Check if blocked by safety
  const isBlockedBySafety = checkpoint.currentStatus === 'CORRUPTED' || !checkpoint.canRollback;

  return {
    id: checkpoint.id,
    displayName: checkpoint.checkpointName || `Checkpoint ${checkpoint.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Checkpoint',
    safetyLevel,
    requiresDualApproval: false,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety,
    domainData: checkpoint,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// TRANSACTION NORMALIZER (DRR-027)
// ============================================================================

/**
 * Map GqlTransactionStatus to UnifiedStatus.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapTransactionStatus(status: string): UnifiedStatus {
  switch (status) {
    case 'ACTIVE':
      return UnifiedStatus.ACTIVE;
    case 'CREATED':
      return UnifiedStatus.PENDING;
    case 'COMMITTED':
      return UnifiedStatus.COMPLETED;
    case 'FAILED':
    case 'ABORTED':
      return UnifiedStatus.FAILED;
    case 'ROLLED_BACK':
      return UnifiedStatus.CANCELLED;
    default:
      // Safe fallback for unknown transaction status values
      return UnifiedStatus.PENDING;
  }
}

/**
 * Normalize a FERP Transaction into NormalizedEntity<Transaction>
 *
 * @param transaction - The Transaction from GraphQL
 * @returns Fully typed NormalizedEntity with Transaction preserved in domainData
 */
export function normalizeTransaction(transaction: Transaction): NormalizedEntity<Transaction> {
  // Use isLifeCritical flag if set
  const safetyLevel = transaction.isLifeCritical
    ? SafetyLevel.LIFE_CRITICAL
    : SafetyLevel.HIGH;

  // Map transaction status to unified status
  const unifiedStatus = mapTransactionStatus(transaction.status);

  // Map compliance frameworks
  const complianceFrameworks = mapComplianceFrameworks(transaction.complianceFrameworks);

  // Check if blocked by safety
  const isBlockedBySafety = transaction.safetyValidationRequired || transaction.safetyApprovalRequired;

  return {
    id: transaction.transactionId,
    displayName: transaction.transactionName || transaction.transactionCode || `Transaction ${transaction.transactionId}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Transaction',
    safetyLevel,
    requiresDualApproval: transaction.safetyApprovalRequired,
    complianceFrameworks,
    unifiedStatus,
    isBlockedBySafety,
    domainData: transaction,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// AUDIT NORMALIZER (DRR-027)
// ============================================================================

/**
 * Normalize a FERP AuditQl into NormalizedEntity<AuditQl>
 *
 * @param audit - The AuditQl from GraphQL
 * @returns Fully typed NormalizedEntity with AuditQl preserved in domainData
 */
export function normalizeAudit(audit: AuditQl): NormalizedEntity<AuditQl> {
  // Audits are CRITICAL safety by default (they're compliance evidence)
  const safetyLevel = SafetyLevel.CRITICAL;

  // Map compliance status to unified status
  // GqlComplianceStatus has: COMPLIANT, EXPIRED, NON_COMPLIANT, PENDING_REVIEW
  let unifiedStatus: UnifiedStatus;
  switch (audit.complianceStatus) {
    case 'COMPLIANT':
      unifiedStatus = UnifiedStatus.COMPLETED;
      break;
    case 'NON_COMPLIANT':
    case 'EXPIRED':
      unifiedStatus = UnifiedStatus.FAILED;
      break;
    case 'PENDING_REVIEW':
      unifiedStatus = UnifiedStatus.PENDING;
      break;
    default:
      unifiedStatus = UnifiedStatus.PENDING;
  }

  // Map regulatory frameworks to compliance frameworks
  const complianceFrameworks = mapComplianceFrameworks(audit.regulatoryFrameworks);

  return {
    id: audit.id,
    displayName: audit.auditName || audit.auditCode || `Audit ${audit.id}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'AuditQL',
    safetyLevel,
    requiresDualApproval: audit.approvalRequired,
    complianceFrameworks,
    unifiedStatus,
    isBlockedBySafety: audit.violationsDetected > 0,
    domainData: audit,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// ANALYTICS NORMALIZER (DRR-027)
// ============================================================================

/**
 * Normalize a FERP Analytics into NormalizedEntity<Analytics>
 *
 * @param analytics - The Analytics from GraphQL
 * @returns Fully typed NormalizedEntity with Analytics preserved in domainData
 */
export function normalizeAnalytics(analytics: Analytics): NormalizedEntity<Analytics> {
  // Determine safety level based on threshold breach configuration
  const safetyLevel = analytics.criticalThreshold !== null && analytics.criticalThreshold !== undefined
    ? SafetyLevel.HIGH
    : SafetyLevel.MEDIUM;

  // Map enabled status to unified status
  let unifiedStatus: UnifiedStatus;
  if (!analytics.enabled) {
    unifiedStatus = UnifiedStatus.CANCELLED;
  } else if (analytics.lastError) {
    unifiedStatus = UnifiedStatus.FAILED;
  } else {
    unifiedStatus = UnifiedStatus.ACTIVE;
  }

  return {
    id: analytics.analyticsId,
    displayName: analytics.analyticsName || analytics.metricName || `Analytics ${analytics.analyticsId}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Analytics',
    safetyLevel,
    requiresDualApproval: false,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety: false,
    domainData: analytics,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// RESOURCE NORMALIZER (DRR-027)
// ============================================================================

/**
 * Map GqlResourceStatus to UnifiedStatus.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapResourceStatus(status: string): UnifiedStatus {
  switch (status) {
    case 'AVAILABLE':
    case 'IN_USE':
      return UnifiedStatus.ACTIVE;
    case 'RESERVED':
      return UnifiedStatus.PENDING;
    case 'MAINTENANCE':
    case 'QUARANTINED':
    case 'UNAVAILABLE':
      return UnifiedStatus.BLOCKED;
    case 'DECOMMISSIONED':
    case 'RETIRED':
      return UnifiedStatus.CANCELLED;
    case 'DEPLETED':
      return UnifiedStatus.FAILED;
    default:
      // Safe fallback for unknown resource status values
      return UnifiedStatus.PENDING;
  }
}

/**
 * Normalize a FERP Resource into NormalizedEntity<Resource>
 *
 * @param resource - The Resource from GraphQL
 * @returns Fully typed NormalizedEntity with Resource preserved in domainData
 */
export function normalizeResource(resource: Resource): NormalizedEntity<Resource> {
  // Use isLifeCriticalResource flag if set
  const safetyLevel = resource.isLifeCriticalResource
    ? SafetyLevel.LIFE_CRITICAL
    : SafetyLevel.MEDIUM;

  // Map resource status to unified status
  const unifiedStatus = mapResourceStatus(resource.resourceStatus);

  // Check if blocked by safety
  const isBlockedBySafety = resource.resourceStatus === 'QUARANTINED' ||
    resource.resourceStatus === 'UNAVAILABLE' ||
    resource.resourceStatus === 'MAINTENANCE';

  return {
    id: resource.resourceId,
    displayName: resource.resourceName || resource.resourceCode || `Resource ${resource.resourceId}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Resource',
    safetyLevel,
    requiresDualApproval: resource.requiresApprovalForUse,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety,
    domainData: resource,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// INTEGRATION NORMALIZER (DRR-027)
// ============================================================================

/**
 * Map IntegrationHealthStatus to UnifiedStatus.
 * Uses string parameter to handle GraphQL codegen's %future added value pattern.
 */
function mapIntegrationHealthStatus(status: string): UnifiedStatus {
  switch (status) {
    case 'HEALTHY':
      return UnifiedStatus.ACTIVE;
    case 'DEGRADED':
      return UnifiedStatus.BLOCKED;
    case 'UNHEALTHY':
      return UnifiedStatus.FAILED;
    case 'UNKNOWN':
      return UnifiedStatus.PENDING;
    default:
      // Safe fallback for unknown integration health status values
      return UnifiedStatus.PENDING;
  }
}

/**
 * Normalize a FERP Integration into NormalizedEntity<Integration>
 *
 * @param integration - The Integration from GraphQL
 * @returns Fully typed NormalizedEntity with Integration preserved in domainData
 */
export function normalizeIntegration(integration: Integration): NormalizedEntity<Integration> {
  // Use isLifeCritical flag if set
  const safetyLevel = integration.isLifeCritical
    ? SafetyLevel.LIFE_CRITICAL
    : SafetyLevel.HIGH;

  // Map health status to unified status
  const unifiedStatus = mapIntegrationHealthStatus(integration.currentHealthStatus);

  // Check if blocked by safety (circuit breaker open or unhealthy)
  const isBlockedBySafety = integration.circuitBreakerState === 'OPEN' ||
    integration.currentHealthStatus === 'UNHEALTHY';

  return {
    id: integration.integrationId,
    displayName: integration.integrationName || integration.integrationCode || `Integration ${integration.integrationId}`,
    sourceSubgraph: 'ferp-core',
    sourceType: 'Integration',
    safetyLevel,
    requiresDualApproval: integration.isLifeCritical,
    complianceFrameworks: [],
    unifiedStatus,
    isBlockedBySafety,
    domainData: integration,
    normalizedAt: new Date().toISOString(),
  };
}

// ============================================================================
// TYPE GUARDS
// ============================================================================

/**
 * Check if an object is a Task
 */
export function isTask(obj: unknown): obj is Task {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Task'
  );
}

/**
 * Check if an object is a Workflow
 */
export function isWorkflow(obj: unknown): obj is Workflow {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Workflow'
  );
}

/**
 * Check if an object is an Actor
 */
export function isActor(obj: unknown): obj is Actor {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Actor'
  );
}

/**
 * Check if an object is a Queue
 */
export function isQueue(obj: unknown): obj is Queue {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Queue'
  );
}

/**
 * Check if an object is a Constraint
 */
export function isConstraint(obj: unknown): obj is Constraint {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Constraint'
  );
}

/**
 * Check if an object is a Notification
 */
export function isNotification(obj: unknown): obj is Notification {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Notification'
  );
}

/**
 * Check if an object is a Policy
 */
export function isPolicy(obj: unknown): obj is Policy {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Policy'
  );
}

/**
 * Check if an object is a Schedule
 */
export function isSchedule(obj: unknown): obj is Schedule {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Schedule'
  );
}

/**
 * Check if an object is an Event
 */
export function isEvent(obj: unknown): obj is Event {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Event'
  );
}

/**
 * Check if an object is a Contract
 */
export function isContract(obj: unknown): obj is Contract {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Contract'
  );
}

/**
 * Check if an object is a Dependency
 */
export function isDependency(obj: unknown): obj is Dependency {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Dependency'
  );
}

// ============================================================================
// TYPE GUARDS FOR DRR-027 PRIMITIVES
// ============================================================================

/**
 * Check if an object is a Template
 */
export function isTemplate(obj: unknown): obj is Template {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Template'
  );
}

/**
 * Check if an object is a Role
 */
export function isRole(obj: unknown): obj is Role {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Role'
  );
}

/**
 * Check if an object is a Team
 */
export function isTeam(obj: unknown): obj is Team {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Team'
  );
}

/**
 * Check if an object is a Validation
 */
export function isValidation(obj: unknown): obj is Validation {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Validation'
  );
}

/**
 * Check if an object is an Instance
 */
export function isInstance(obj: unknown): obj is Instance {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Instance'
  );
}

/**
 * Check if an object is an Approval
 */
export function isApproval(obj: unknown): obj is Approval {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Approval'
  );
}

/**
 * Check if an object is a Document
 */
export function isDocument(obj: unknown): obj is Document {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Document'
  );
}

/**
 * Check if an object is a Trace
 */
export function isTrace(obj: unknown): obj is Trace {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Trace'
  );
}

/**
 * Check if an object is a Checkpoint
 */
export function isCheckpoint(obj: unknown): obj is Checkpoint {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Checkpoint'
  );
}

/**
 * Check if an object is a Transaction
 */
export function isTransaction(obj: unknown): obj is Transaction {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Transaction'
  );
}

/**
 * Check if an object is an AuditQl
 */
export function isAudit(obj: unknown): obj is AuditQl {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'AuditQL'
  );
}

/**
 * Check if an object is an Analytics
 */
export function isAnalytics(obj: unknown): obj is Analytics {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Analytics'
  );
}

/**
 * Check if an object is a Resource
 */
export function isResource(obj: unknown): obj is Resource {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Resource'
  );
}

/**
 * Check if an object is an Integration
 */
export function isIntegration(obj: unknown): obj is Integration {
  return (
    typeof obj === 'object' &&
    obj !== null &&
    '__typename' in obj &&
    (obj as { __typename: string }).__typename === 'Integration'
  );
}
